import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import IconButton from '../../../atoms/IconButton/IconButton';
import Picto from '../../../atoms/Picto/Picto';

// Domain
import { applyFieldsToUpdate } from '../../../../domain/form/formUseCase';

// Services
import { resolveEntityPanelFormUrl } from '../../../../services/forms/formMockConfig';

// Store
import { useFormPanelContextOptional } from '../../../../store/formPanel/FormPanelContext';

// Utils
import cn from '../../../../utils/cn';

// Styles
import styles from './EntityField.module.css';

/**
 * @typedef {object} EntityFieldValue
 * @property {string[]} [values] - Entity ids
 * @property {string[]} [displayedValues] - Entity display labels
 */

/**
 * One entity row (filled or empty add affordance).
 * @param {object} props
 * @param {string} props.label
 * @param {boolean} props.hasEntity
 * @param {boolean} props.disabled
 * @param {boolean} props.isReadOnly
 * @param {() => void} props.onOpen
 * @param {() => void} [props.onClear]
 * @param {string} props.editTitle
 * @param {string} props.clearTitle
 * @returns {JSX.Element}
 */
function EntityFieldRow({
  label,
  hasEntity,
  disabled,
  isReadOnly,
  onOpen,
  onClear,
  editTitle,
  clearTitle,
}) {
  const handleKeyDown = (/** @type {React.KeyboardEvent} */ event) => {
    if (disabled || isReadOnly) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  const handleEditClick = (/** @type {React.MouseEvent} */ event) => {
    event.stopPropagation();
    onOpen();
  };

  const handleClearClick = (/** @type {React.MouseEvent} */ event) => {
    event.stopPropagation();
    onClear?.();
  };

  return (
    <div
      role="button"
      tabIndex={disabled || isReadOnly ? -1 : 0}
      className={cn([
        styles.field,
        !disabled && !isReadOnly && styles['field-interactive'],
        disabled && styles['field-disabled'],
        isReadOnly && styles['field-read-only'],
      ])}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled || isReadOnly}
    >
      <span className={styles.label}>{label}</span>

      {!hasEntity && !isReadOnly ? (
        <Picto
          icon="add-square-outline"
          className={styles['add-icon']}
          aria-hidden
        />
      ) : null}

      {hasEntity && !isReadOnly ? (
        <div className={styles.actions}>
          <IconButton
            color="no-background"
            size="medium"
            icon="edit-2"
            iconColor="neutral"
            disabled={disabled}
            title={editTitle}
            className={styles['action-button']}
            onClick={handleEditClick}
          />
          <IconButton
            color="no-background"
            size="small"
            icon="trash-empty"
            iconColor="neutral"
            disabled={disabled}
            title={clearTitle}
            className={styles['action-button']}
            onClick={handleClearClick}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Entity picker field — displays selected entity or empty state with add/search affordance.
 * @param {object} props
 * @param {EntityFieldValue} [props.value] - Current field value
 * @param {string} props.baseFormUrl - Base URL to load the entity form (add or edit)
 * @param {boolean} [props.multiple] - Whether several entities can be selected
 * @param {boolean} [props.disabled] - Whether the field is disabled
 * @param {boolean} [props.isReadOnly] - Locked; value remains in form
 * @param {{ message?: string } | string | null} [props.error] - Validation error
 * @param {string} [props.id] - Field id for accessibility
 * @param {string} [props.className] - Additional CSS classes
 * @param {(value: EntityFieldValue | null) => void} [props.onChange] - Field value change handler
 * @param {import('react-hook-form').UseFormSetValue<Record<string, unknown>>} [props.setValue] - Parent form setValue
 * @param {import('react-hook-form').UseFormGetValues<Record<string, unknown>>} [props.getValues] - Parent form getValues
 * @param {FormLayout} [props.layout] - Parent form layout (fieldsById)
 * @returns {JSX.Element} EntityField component
 */
function EntityField({
  value,
  onChange,
  baseFormUrl,
  multiple = false,
  disabled = false,
  isReadOnly = false,
  error = null,
  id,
  className = '',
  setValue,
  getValues,
  layout,
}) {
  const { t } = useTranslation();
  const formPanel = useFormPanelContextOptional();
  const { values = [], displayedValues = [] } = value ?? {};
  const errorMessage = typeof error === 'string' ? error : error?.message;
  const emptyLabel = t('form.entity.empty');
  const editTitle = t('form.entity.edit');
  const clearTitle = t('form.entity.clear');
  const showAddRow = !isReadOnly && (multiple || values.length === 0);

  const openEntityForm = useCallback((entityId = '') => {
    if (disabled || isReadOnly || !formPanel) return;
    const formUrl = resolveEntityPanelFormUrl(Boolean(entityId), baseFormUrl, entityId);

    const onChildSuccess = (/** @type {ApiFormFieldsToUpdateItem[]} */ fieldsToUpdate) => {
      if (setValue && layout) {
        applyFieldsToUpdate(fieldsToUpdate, setValue, layout, getValues);
      }
    };

    formPanel.openFormPanel(formUrl, onChildSuccess);
  }, [disabled, isReadOnly, formPanel, baseFormUrl, layout, setValue, getValues]);

  const handleClearAt = useCallback((index) => {
    if (disabled || isReadOnly || !onChange) return;
    if (!multiple) {
      onChange(null);
      return;
    }
    onChange({
      values: values.filter((_, i) => i !== index),
      displayedValues: displayedValues.filter((_, i) => i !== index),
    });
  }, [disabled, isReadOnly, onChange, multiple, values, displayedValues]);

  return (
    <div
      className={cn([styles.wrapper, className])}
      id={id}
      data-base-form-url={baseFormUrl}
    >
      {values.map((entityId, index) => (
        <EntityFieldRow
          key={entityId}
          label={displayedValues[index] || entityId}
          hasEntity
          disabled={disabled}
          isReadOnly={isReadOnly}
          onOpen={() => openEntityForm(entityId)}
          onClear={() => handleClearAt(index)}
          editTitle={editTitle}
          clearTitle={clearTitle}
        />
      ))}

      {showAddRow ? (
        <EntityFieldRow
          label={emptyLabel}
          hasEntity={false}
          disabled={disabled}
          isReadOnly={isReadOnly}
          onOpen={() => openEntityForm()}
          editTitle={editTitle}
          clearTitle={clearTitle}
        />
      ) : null}

      {errorMessage ? (
        <div className={styles.error} role="alert">
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}

export default EntityField;
